import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../../../contexts/UserContext";
import PATHS from "../../../constants/Paths";

/**
 * LoginFormSection - User login form component
 *
 * Handles user authentication with email and password inputs, remember me checkbox,
 * forgot password link, and form submission. Integrates with UserContext for
 * authentication state management.
 *
 * Features:
 * - Email and password validation
 * - Remember me functionality (persistent login)
 * - Error handling and display
 * - Forgot password placeholder
 * - Automatic redirect to dashboard on successful login
 *
 * @returns {JSX.Element} Complete login form with validation and error handling
 */
export default function LoginFormSection() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const { login } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(""); // reset previous error
    try {
      await login({ email, password }, remember);

      // Redirect to intended page or dashboard
      const from = location.state?.from?.pathname || PATHS.DASHBOARD;
      navigate(from, { replace: true });
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <form className="flex flex-col gap-4 w-full" onSubmit={handleSubmit}>
      {error && <p className="text-sm text-red-600 text-center">{error}</p>}
      <div>
        <input
          type="email"
          placeholder="Email"
          className="w-full px-4 py-2 border border-input rounded-lg text-base font-roboto focus:outline-none focus:border-brand-500"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />
      </div>
      <div>
        <input
          type="password"
          placeholder="Password"
          className="w-full px-4 py-2 border border-input rounded-lg text-base font-roboto focus:outline-none focus:border-brand-500"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      </div>
      <div className="flex items-center justify-between text-sm">
        <label className="flex items-center gap-2 font-roboto">
          <input
            type="checkbox"
            className="accent-black w-4 h-4"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          Remember me
        </label>
        <button
          type="button"
          className="font-roboto font-medium underline bg-transparent border-none cursor-pointer text-sm hover:text-emerald-600"
          onClick={() => navigate("/forgot-password")}
        >
          Forgot Password?
        </button>
      </div>
      <button
        type="submit"
        className="w-full bg-brand-600 text-white rounded-lg py-2 text-base font-semibold font-roboto mt-1"
      >
        Sign in
      </button>
    </form>
  );
}
